import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Plus, Search, Building2, Phone, Mail,
  Calendar, Pencil, Trash2, Check, User, MapPin,
  FileText, DollarSign, ChevronRight, CreditCard, Wallet, Users,
  TrendingUp, AlertTriangle, Clock, CheckCircle,
  SlidersHorizontal, ChevronDown, ChevronUp, RotateCcw, X
} from 'lucide-react';
import { API_URL } from '../config/api';
import logService from '../services/logService';
import useEscapeClose from '../hooks/useEscapeClose';
import PriceInput from '../components/PriceInput';
import './Wholesalers.css';
import './WholesalerPayments.css';

const Wholesalers = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [wholesalers, setWholesalers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWholesaler, setEditingWholesaler] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterHasDebt, setFilterHasDebt] = useState('all');
  const [filterSortBy, setFilterSortBy] = useState('name');

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showModal, () => setShowModal(false)],
  ]);

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Para birimi bilgileri
  const currencyInfo = {
    TRY: { name: 'Türk Lirası', symbol: '₺', icon: '🇹🇷' },
    USD: { name: 'Amerikan Doları', symbol: '$', icon: '🇺🇸' },
    EUR: { name: 'Euro', symbol: '€', icon: '🇪🇺' },
    GOLD: { name: 'Altın (gram)', symbol: 'gr', icon: '🥇' },
    SILVER: { name: 'Gümüş (gram)', symbol: 'gr', icon: '🥈' }
  };

  // Ödeme yöntemi bilgileri
  const paymentMethodInfo = {
    cash: { name: 'Nakit', icon: '🧾', color: '#22c55e' },
    card: { name: 'Kart', icon: '💳', color: '#3b82f6' },
    gold: { name: 'Altın', icon: '🥇', color: '#f59e0b' },
    silver: { name: 'Gümüş', icon: '🥈', color: '#94a3b8' },
    bank_transfer: { name: 'Havale/EFT', icon: '🏦', color: '#8b5cf6' },
    other: { name: 'Diğer', icon: '📋', color: '#64748b' }
  };

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    company_name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    tax_number: '',
    bank_name: '',
    iban: '',
    payment_terms: 0,
    credit_limit: 0,
    notes: '',
    active: true
  });

  useEffect(() => {
    fetchData();
  }, [filterActive]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterActive !== 'all') {
        params.append('active', filterActive);
      }

      // Paralel istekler
      const [wholesalersRes, paymentsRes] = await Promise.all([
        fetch(`${API_URL}/wholesalers?${params}`),
        fetch(`${API_URL}/wholesaler-payments`)
      ]);

      const wholesalersData = await wholesalersRes.json();
      const paymentsData = await paymentsRes.json();

      setWholesalers(Array.isArray(wholesalersData) ? wholesalersData : []);
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
    } catch (error) {
      console.error('Veriler yüklenemedi:', error);
      toast.error('Veriler yüklenemedi');
      setWholesalers([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWholesalers = () => fetchData();

  const resetForm = () => {
    setEditingWholesaler(null);
    setFormData({
      code: '',
      name: '',
      company_name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      tax_number: '',
      bank_name: '',
      iban: '',
      payment_terms: 0,
      credit_limit: 0,
      notes: '',
      active: true
    });
  };

  const openNewModal = () => {
    resetForm();
    // Otomatik kod oluştur
    const existingCodes = wholesalers.map(w => w.code).filter(c => c && c.startsWith('TPC'));
    const maxNum = existingCodes.reduce((max, code) => {
      const num = parseInt(code.replace('TPC', ''));
      return num > max ? num : max;
    }, 0);
    setFormData(prev => ({ ...prev, code: `TPC${String(maxNum + 1).padStart(4, '0')}` }));
    setShowModal(true);
  };

  const openEditModal = (wholesaler) => {
    setEditingWholesaler(wholesaler);
    setFormData({
      code: wholesaler.code || '',
      name: wholesaler.name || '',
      company_name: wholesaler.company_name || '',
      contact_person: wholesaler.contact_person || '',
      phone: wholesaler.phone || '',
      email: wholesaler.email || '',
      address: wholesaler.address || '',
      tax_number: wholesaler.tax_number || '',
      bank_name: wholesaler.bank_name || '',
      iban: wholesaler.iban || '',
      payment_terms: wholesaler.payment_terms || 30,
      credit_limit: wholesaler.credit_limit || 0,
      notes: wholesaler.notes || '',
      active: wholesaler.active === 1 || wholesaler.active === true
    });
    setShowModal(true);
  };

  // Dış sayfadan editId ile gelince otomatik düzenleme modalı aç
  useEffect(() => {
    if (location.state?.editId && wholesalers.length > 0) {
      const w = wholesalers.find(ws => ws.id === location.state.editId);
      if (w) openEditModal(w);
      // State'i temizle
      window.history.replaceState({}, document.title);
    }
  }, [location.state, wholesalers]);

  const handleSave = async () => {
    if (!formData.name) {
      toast.warning('Toptancı adı zorunludur!');
      return;
    }

    try {
      const url = editingWholesaler
        ? `${API_URL}/wholesalers/${editingWholesaler.id}`
        : `${API_URL}/wholesalers`;
      const method = editingWholesaler ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          active: formData.active ? 1 : 0
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(editingWholesaler ? 'Toptancı güncellendi!' : 'Toptancı oluşturuldu!');
        // Log kaydet
        logService.log('wholesalers', editingWholesaler ? 'update' : 'create',
          editingWholesaler
            ? `Toptancı güncellendi: ${formData.name}`
            : `Yeni toptancı eklendi: ${formData.name}`, {
          entity_type: 'wholesaler',
          entity_id: editingWholesaler?.id || data.id,
          status: 'success',
          details: {
            name: formData.name,
            company_name: formData.company_name,
            phone: formData.phone,
            email: formData.email
          }
        });
        setShowModal(false);
        resetForm();
        fetchWholesalers();
      } else {
        toast.error(data.error || 'İşlem başarısız!');
        logService.log('wholesalers', editingWholesaler ? 'update' : 'create',
          `Toptancı ${editingWholesaler ? 'güncellenemedi' : 'eklenemedi'}: ${formData.name}`, {
          status: 'error',
          details: { error: data.error }
        });
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('wholesalers', editingWholesaler ? 'update' : 'create',
        `Toptancı kayıt hatası: ${formData.name}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  const handleDelete = async (wholesaler) => {
    showConfirmation({
      title: 'Toptancı Sil',
      message: `"${wholesaler.name}" toptancısını silmek istediğinize emin misiniz?`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/wholesalers/${wholesaler.id}`, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            toast.success('Toptancı silindi!');
            // Log kaydet
            logService.log('wholesalers', 'delete', `Toptancı silindi: ${wholesaler.name}`, {
              entity_type: 'wholesaler',
              entity_id: wholesaler.id,
              status: 'warning',
              details: {
                name: wholesaler.name,
                company_name: wholesaler.company_name,
                phone: wholesaler.phone
              }
            });
            fetchWholesalers();
          } else {
            toast.error(data.error || 'Silme başarısız!');
            logService.log('wholesalers', 'delete', `Toptancı silinemedi: ${wholesaler.name}`, {
              status: 'error',
              details: { error: data.error }
            });
          }
        } catch (error) {
          console.error('Silme hatası:', error);
          toast.error('Silme başarısız!');
          logService.log('wholesalers', 'delete', `Toptancı silme hatası: ${wholesaler.name}`, {
            status: 'error',
            details: { error: error.message }
          });
        }
      }
    });
  };

  const formatCurrency = (amount) => {
    return `₺${Number(amount || 0).toLocaleString()}`;
  };

  const activeFilterCount = [filterCategory, filterHasDebt].filter(f => f !== 'all').length;
  const resetFilters = () => { setFilterCategory('all'); setFilterHasDebt('all'); setFilterSortBy('name'); setSearchTerm(''); };

  const filteredWholesalers = wholesalers.filter(w => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesName = w.name?.toLowerCase().includes(search);
      const matchesCompany = w.company_name?.toLowerCase().includes(search);
      const matchesPhone = w.phone?.toLowerCase().includes(search);
      const matchesEmail = w.email?.toLowerCase().includes(search);
      if (!matchesName && !matchesCompany && !matchesPhone && !matchesEmail) return false;
    }
    // Borç filtresi
    if (filterHasDebt === 'has_debt') {
      const hasDebt = payments.some(p => p.wholesaler_id === w.id && (p.status === 'pending' || p.status === 'partial'));
      if (!hasDebt) return false;
    } else if (filterHasDebt === 'no_debt') {
      const hasDebt = payments.some(p => p.wholesaler_id === w.id && (p.status === 'pending' || p.status === 'partial'));
      if (hasDebt) return false;
    }
    return true;
  }).sort((a, b) => {
    if (filterSortBy === 'name') return (a.name || '').localeCompare(b.name || '', 'tr');
    if (filterSortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '', 'tr');
    const getWsTotal = (wId, type) => {
      return payments.filter(p => p.wholesaler_id === wId).reduce((s, p) => {
        const amt = Number(p.amount) || 0;
        const paidAmt = p.status === 'paid' ? amt : (Number(p.paid_amount) || 0);
        if (type === 'debt') return s + (p.payment_type === 'expense' && (p.status === 'pending' || p.status === 'partial') ? amt - paidAmt : 0);
        if (type === 'credit') return s + (p.payment_type === 'income' ? amt : 0);
        if (type === 'paid') return s + paidAmt;
        return s;
      }, 0);
    };
    if (filterSortBy === 'debt_desc') return getWsTotal(b.id, 'debt') - getWsTotal(a.id, 'debt');
    if (filterSortBy === 'debt_asc') return getWsTotal(a.id, 'debt') - getWsTotal(b.id, 'debt');
    if (filterSortBy === 'credit_desc') return getWsTotal(b.id, 'credit') - getWsTotal(a.id, 'credit');
    if (filterSortBy === 'credit_asc') return getWsTotal(a.id, 'credit') - getWsTotal(b.id, 'credit');
    if (filterSortBy === 'paid_desc') return getWsTotal(b.id, 'paid') - getWsTotal(a.id, 'paid');
    if (filterSortBy === 'paid_asc') return getWsTotal(a.id, 'paid') - getWsTotal(b.id, 'paid');
    if (filterSortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    return 0;
  });

  // Detaylı İstatistikler
  const getDetailedStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Arşivlenmemiş ödemeler
    const activePayments = payments.filter(p => !p.archived);

    // Bekleyen ödemeler (borç)
    const pendingExpense = activePayments
      .filter(p => p.status === 'pending' && p.payment_type === 'expense')
      .reduce((sum, p) => sum + Number(p.amount), 0);

    // Bekleyen alacaklar
    const pendingIncome = activePayments
      .filter(p => p.status === 'pending' && p.payment_type === 'income')
      .reduce((sum, p) => sum + Number(p.amount), 0);

    // Ödenen toplam
    const totalPaid = activePayments
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + Number(p.paid_amount || p.amount), 0);

    // Gecikmiş ödemeler (null due_date kontrolü)
    const overduePayments = activePayments.filter(p => {
      if (p.status !== 'pending') return false;
      if (!p.due_date) return false;
      const dueDate = new Date(p.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    });
    const overdueAmount = overduePayments.reduce((sum, p) => sum + Number(p.amount), 0);

    // Para birimi bazlı özet
    const currencySummary = {};
    activePayments.forEach(p => {
      const curr = p.currency || 'TRY';
      if (!currencySummary[curr]) {
        currencySummary[curr] = { pending: 0, paid: 0, income: 0 };
      }
      if (p.status === 'pending' && p.payment_type === 'expense') {
        currencySummary[curr].pending += Number(p.amount);
      }
      if (p.status === 'pending' && p.payment_type === 'income') {
        currencySummary[curr].income += Number(p.amount);
      }
      if (p.status === 'paid') {
        currencySummary[curr].paid += Number(p.paid_amount || p.amount);
      }
    });

    // Ödeme yöntemi bazlı özet
    const methodSummary = {};
    activePayments.filter(p => p.status === 'paid' && p.payment_method).forEach(p => {
      const method = p.payment_method;
      if (!methodSummary[method]) {
        methodSummary[method] = { count: 0, total: 0 };
      }
      methodSummary[method].count += 1;
      methodSummary[method].total += Number(p.paid_amount || p.amount);
    });

    return {
      total: wholesalers.length,
      active: wholesalers.filter(w => w.active).length,
      pendingExpense,
      pendingIncome,
      totalPaid,
      overdueCount: overduePayments.length,
      overdueAmount,
      netBalance: pendingIncome - pendingExpense,
      currencySummary,
      methodSummary,
      totalPayments: activePayments.length,
      paidPayments: activePayments.filter(p => p.status === 'paid').length,
      pendingPayments: activePayments.filter(p => p.status === 'pending').length
    };
  };

  const stats = getDetailedStats();

  const formatCurrencyByType = (amount, currency = 'TRY') => {
    const info = currencyInfo[currency] || currencyInfo.TRY;
    if (currency === 'GOLD' || currency === 'SILVER') {
      return `${Number(amount || 0).toLocaleString()} ${info.symbol}`;
    }
    return `${info.symbol}${Number(amount || 0).toLocaleString()}`;
  };

  // Toptancı bazlı para birimi özetini hesapla
  const getWholesalerCurrencyDebts = (wholesalerId) => {
    const wholesalerPayments = payments.filter(p => p.wholesaler_id === wholesalerId && p.status === 'pending');
    const summary = {};

    wholesalerPayments.forEach(p => {
      const curr = p.currency || 'TRY';
      if (!summary[curr]) {
        summary[curr] = { debt: 0, credit: 0 };
      }
      if (p.payment_type === 'expense') {
        summary[curr].debt += Number(p.amount || 0);
      } else {
        summary[curr].credit += Number(p.amount || 0);
      }
    });

    return summary;
  };

  // Toptancı toplam ödenen hesapla
  const getWholesalerTotalPaid = (wholesalerId) => {
    const wholesalerPayments = payments.filter(p => p.wholesaler_id === wholesalerId);
    const summary = {};

    wholesalerPayments.forEach(p => {
      const curr = p.currency || 'TRY';
      if (!summary[curr]) summary[curr] = 0;
      summary[curr] += Number(p.paid_amount || 0);
    });

    return summary;
  };

  if (loading) {
    return (
      <div className="ws-container">
        <div className="ws-loading">
          <div className="ws-loading-spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-container">
      {/* Header */}
      <header className="ws-header">
        <div className="ws-header-left">
          <button className="ws-back-btn" onClick={() => navigate('/wholesaler-payments')}>
            <ArrowLeft size={18} /> Ödeme Takibi
          </button>
          <h1 className="ws-title">Toptancılar</h1>
        </div>
        <div className="ws-header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Logo"
            className="ws-logo"
          />
        </div>
        <div className="ws-header-right">
          <button className="ws-header-btn primary" onClick={openNewModal}>
            <Plus size={18} /> Yeni Toptancı
          </button>
        </div>
      </header>

      {/* Dashboard */}
      <div className="ws-dashboard">
        <div className="ws-stat-card total">
          <span className="ws-stat-top">Toptancılar</span>
          <div className="ws-stat-tags">
            <span className="ws-stat-tag">{stats.total} toplam</span>
            <span className="ws-stat-tag">{stats.active} aktif</span>
          </div>
        </div>

        <div className="ws-stat-card expense">
          <span className="ws-stat-top">Toplam Borç</span>
          <div className="ws-stat-tags">
            {Object.entries(stats.currencySummary).filter(([, d]) => d.pending > 0).map(([curr, data]) => (
              <span key={curr} className="ws-stat-tag">{formatCurrencyByType(data.pending, curr)}</span>
            ))}
            {Object.entries(stats.currencySummary).filter(([, d]) => d.pending > 0).length === 0 && (
              <span className="ws-stat-tag">₺0</span>
            )}
          </div>
        </div>

        <div className="ws-stat-card income">
          <span className="ws-stat-top">Toplam Alacak</span>
          <div className="ws-stat-tags">
            {Object.entries(stats.currencySummary).filter(([, d]) => d.income > 0).map(([curr, data]) => (
              <span key={curr} className="ws-stat-tag">{formatCurrencyByType(data.income, curr)}</span>
            ))}
            {Object.entries(stats.currencySummary).filter(([, d]) => d.income > 0).length === 0 && (
              <span className="ws-stat-tag">₺0</span>
            )}
          </div>
        </div>

        <div className="ws-stat-card paid-stat">
          <span className="ws-stat-top">Ödenen</span>
          <div className="ws-stat-tags">
            {Object.entries(stats.currencySummary).filter(([, d]) => d.paid > 0).map(([curr, data]) => (
              <span key={curr} className="ws-stat-tag">{formatCurrencyByType(data.paid, curr)}</span>
            ))}
            {Object.entries(stats.currencySummary).filter(([, d]) => d.paid > 0).length === 0 && (
              <span className="ws-stat-tag">₺0</span>
            )}
          </div>
        </div>

        <div className="ws-stat-card overdue">
          <span className="ws-stat-top">Gecikmiş</span>
          <div className="ws-stat-tags">
            <span className="ws-stat-tag">{stats.overdueCount} ödeme</span>
          </div>
        </div>
      </div>

      {/* Tabs + Arama */}
      <div className="ws-tabs-filter">
        <div className="ws-tabs-row">
          <div className="ws-tabs">
            <button
              className={`ws-tab ${filterActive === 'all' ? 'active' : ''}`}
              onClick={() => setFilterActive('all')}
            >
              <span className="ws-tab-label">Tümü</span>
              <span className="ws-tab-count">{wholesalers.length}</span>
            </button>
            <button
              className={`ws-tab green ${filterActive === 'true' ? 'active' : ''}`}
              onClick={() => setFilterActive('true')}
            >
              <span className="ws-tab-label">Aktif</span>
              <span className="ws-tab-count">{wholesalers.filter(w => w.active).length}</span>
            </button>
            <button
              className={`ws-tab red ${filterActive === 'false' ? 'active' : ''}`}
              onClick={() => setFilterActive('false')}
            >
              <span className="ws-tab-label">Pasif</span>
              <span className="ws-tab-count">{wholesalers.filter(w => !w.active).length}</span>
            </button>
          </div>

          <div className="wp-search">
            <Search size={16} />
            <input type="text" placeholder="Toptancı ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {searchTerm && <button className="wp-search-clear" onClick={() => setSearchTerm('')}>×</button>}
          </div>

          <button className={`wp-filter-toggle ${showFilters ? 'active' : ''} ${activeFilterCount > 0 ? 'has-filters' : ''}`} onClick={() => setShowFilters(!showFilters)}>
            <SlidersHorizontal size={15} />
            <span>Filtre</span>
            {activeFilterCount > 0 && <span className="wp-filter-toggle-count">{activeFilterCount}</span>}
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {showFilters && (
          <div className="wp-filters-panel">
            <div className="wp-filters-grid">
              <div className="wp-filter-group">
                <label>Borç Durumu</label>
                <select value={filterHasDebt} onChange={e => setFilterHasDebt(e.target.value)}>
                  <option value="all">Tümü</option>
                  <option value="has_debt">Borcu Olan</option>
                  <option value="no_debt">Borcu Olmayan</option>
                </select>
              </div>
              <div className="wp-filter-group">
                <label>Sıralama</label>
                <select value={filterSortBy} onChange={e => setFilterSortBy(e.target.value)}>
                  <option value="name">İsim (A→Z)</option>
                  <option value="name_desc">İsim (Z→A)</option>
                  <option value="debt_desc">Borç (Çok→Az)</option>
                  <option value="debt_asc">Borç (Az→Çok)</option>
                  <option value="credit_desc">Alacak (Çok→Az)</option>
                  <option value="credit_asc">Alacak (Az→Çok)</option>
                  <option value="paid_desc">Ödenen (Çok→Az)</option>
                  <option value="paid_asc">Ödenen (Az→Çok)</option>
                  <option value="newest">Son Eklenen</option>
                </select>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <button className="wp-filters-reset" onClick={resetFilters}>
                <RotateCcw size={13} /> Filtreleri Temizle
              </button>
            )}
          </div>
        )}
      </div>

      {/* Toptancı Listesi */}
      <div className="ws-grid">
        {filteredWholesalers.length === 0 ? (
          <div className="ws-empty">
            <Building2 size={48} />
            <p>{searchTerm ? 'Arama sonucu bulunamadı' : 'Toptancı bulunamadı'}</p>
            {!searchTerm && (
              <button className="ws-add-btn" onClick={openNewModal}>
                <Plus size={16} /> Yeni Toptancı Ekle
              </button>
            )}
          </div>
        ) : (
          filteredWholesalers.map((wholesaler, index) => {
            const initials = (wholesaler.name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const currDebts = getWholesalerCurrencyDebts(wholesaler.id);
            const currencies = Object.keys(currDebts);
            const totalPaid = getWholesalerTotalPaid(wholesaler.id);
            const paidCurrencies = Object.keys(totalPaid).filter(c => totalPaid[c] > 0);

            return (
              <div
                key={wholesaler.id}
                className={`ws-card ${!wholesaler.active ? 'inactive' : ''}`}
                style={{ animationDelay: `${index * 0.04}s` }}
                onClick={() => navigate(`/wholesalers/${wholesaler.id}`)}
              >
                <div className="ws-card-top">
                  <div className="ws-card-left">
                    <div className="ws-card-avatar">{initials}</div>
                    <div className="ws-card-info">
                      <h4 className="ws-card-name">{wholesaler.name}</h4>
                      {wholesaler.company_name && (
                        <span className="ws-card-company">{wholesaler.company_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="ws-card-right-top">
                    <span className={`ws-badge ${wholesaler.active ? 'active' : 'inactive'}`}>
                      {wholesaler.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </div>

                {(wholesaler.phone || wholesaler.contact_person || wholesaler.payment_terms > 0 || wholesaler.email) && (
                <div className="ws-card-details">
                  {wholesaler.contact_person && (
                    <span className="ws-detail-chip">
                      <User size={12} /> {wholesaler.contact_person}
                    </span>
                  )}
                  {wholesaler.phone && (
                    <span className="ws-detail-chip">
                      <Phone size={12} /> {wholesaler.phone}
                    </span>
                  )}
                  {wholesaler.payment_terms > 0 && (
                    <span className="ws-detail-chip">
                      <Calendar size={12} /> {wholesaler.payment_terms} gün vade
                    </span>
                  )}
                </div>
                )}

                <div className="ws-card-footer">
                  <div className="ws-currency-debts">
                    {currencies.length === 0 && paidCurrencies.length === 0 ? (
                      <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>Henüz ödeme kaydı yok</span>
                    ) : (
                      <>
                        {currencies.map(curr => {
                          const data = currDebts[curr];
                          return (
                            <div key={curr} className="ws-debt-tag">
                              <span className="ws-debt-icon">{currencyInfo[curr]?.icon}</span>
                              {data.debt > 0 && <span className="ws-debt-val expense">-{formatCurrencyByType(data.debt, curr)}</span>}
                              {data.credit > 0 && <span className="ws-debt-val income">+{formatCurrencyByType(data.credit, curr)}</span>}
                            </div>
                          );
                        })}
                        {paidCurrencies.map(curr => (
                          <div key={`paid-${curr}`} className="ws-debt-tag" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>Ödenen: {formatCurrencyByType(totalPaid[curr], curr)}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  <div className="ws-card-actions">
                    <button className="ws-action-btn edit" onClick={(e) => { e.stopPropagation(); openEditModal(wholesaler); }} title="Düzenle">
                      <Pencil size={14} />
                    </button>
                    <button className="ws-action-btn delete" onClick={(e) => { e.stopPropagation(); handleDelete(wholesaler); }} title="Sil">
                      <Trash2 size={14} />
                    </button>
                    <ChevronRight size={18} className="ws-arrow" />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="ws-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="ws-modal" onClick={e => e.stopPropagation()}>
            <div className="ws-modal-header">
              <h2>{editingWholesaler ? 'Toptancı Düzenle' : 'Yeni Toptancı'}</h2>
              <button className="ws-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="ws-modal-body">
              <div className="ws-form-section">
                <h3><Building2 size={16} /> Temel Bilgiler</h3>
                <div className="ws-form-row">
                  <div className="ws-form-group">
                    <label>Toptancı Kodu</label>
                    <input type="text" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})} placeholder="Otomatik oluşturulur" />
                  </div>
                  <div className="ws-form-group">
                    <label>Toptancı Adı *</label>
                    <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Toptancı adı..." />
                  </div>
                </div>
                <div className="ws-form-row">
                  <div className="ws-form-group">
                    <label>Firma Adı</label>
                    <input type="text" value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} placeholder="Ticari unvan..." />
                  </div>
                  <div className="ws-form-group">
                    <label>Yetkili Kişi</label>
                    <input type="text" value={formData.contact_person} onChange={e => setFormData({...formData, contact_person: e.target.value})} placeholder="İletişim kurulacak kişi..." />
                  </div>
                </div>
              </div>

              <div className="ws-form-section">
                <h3><Phone size={16} /> İletişim Bilgileri</h3>
                <div className="ws-form-row">
                  <div className="ws-form-group">
                    <label>Telefon</label>
                    <input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="0532 XXX XX XX" />
                  </div>
                  <div className="ws-form-group">
                    <label>E-posta</label>
                    <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="info@firma.com" />
                  </div>
                </div>
                <div className="ws-form-group">
                  <label>Adres</label>
                  <textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Firma adresi..." rows="2" />
                </div>
              </div>

              <div className="ws-form-section">
                <h3><FileText size={16} /> Vergi ve Banka Bilgileri</h3>
                <div className="ws-form-row">
                  <div className="ws-form-group">
                    <label>Vergi Numarası</label>
                    <input type="text" value={formData.tax_number} onChange={e => setFormData({...formData, tax_number: e.target.value})} placeholder="VKN / TCKN" />
                  </div>
                  <div className="ws-form-group">
                    <label>Banka Adı</label>
                    <input type="text" value={formData.bank_name} onChange={e => setFormData({...formData, bank_name: e.target.value})} placeholder="Banka adı..." />
                  </div>
                </div>
                <div className="ws-form-group">
                  <label>IBAN</label>
                  <input type="text" value={formData.iban} onChange={e => setFormData({...formData, iban: e.target.value})} placeholder="TR XX XXXX XXXX XXXX XXXX XXXX XX" />
                </div>
              </div>

              <div className="ws-form-section">
                <h3><CreditCard size={16} /> Ödeme Koşulları</h3>
                <div className="ws-form-row">
                  <div className="ws-form-group">
                    <label>Ödeme Vadesi (gün)</label>
                    <input type="number" value={formData.payment_terms} onChange={e => setFormData({...formData, payment_terms: parseInt(e.target.value) || 0})} min="0" />
                  </div>
                  <div className="ws-form-group">
                    <label>Kredi Limiti</label>
                    <PriceInput value={parseFloat(formData.credit_limit) || 0} onChange={(val) => setFormData({...formData, credit_limit: val})} />
                  </div>
                </div>
              </div>

              <div className="ws-form-group">
                <label>Notlar</label>
                <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Ek notlar..." rows="2" />
              </div>

              <div className="ws-form-group checkbox">
                <label>
                  <input type="checkbox" checked={formData.active} onChange={e => setFormData({...formData, active: e.target.checked})} />
                  <span>Aktif Toptancı</span>
                </label>
              </div>
            </div>

            <div className="ws-modal-footer">
              <button className="ws-btn cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button className="ws-btn save" onClick={handleSave}>
                <Check size={16} /> {editingWholesaler ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onay Modal */}
      {showConfirmModal && (
        <div className="ws-modal-overlay" style={{ zIndex: 10000 }} onClick={() => setShowConfirmModal(false)}>
          <div className="ws-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className={`ws-confirm-header ${confirmModalConfig.type}`}>
              <AlertTriangle size={24} />
              <h3>{confirmModalConfig.title}</h3>
            </div>
            <div className="ws-confirm-body">
              <p>{confirmModalConfig.message}</p>
            </div>
            <div className="ws-confirm-footer">
              <button className="ws-btn cancel" onClick={() => setShowConfirmModal(false)}>{confirmModalConfig.cancelText}</button>
              <button className={`ws-btn ${confirmModalConfig.type === 'danger' ? 'danger' : 'save'}`} onClick={() => { setShowConfirmModal(false); confirmModalConfig.onConfirm(); }}>
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wholesalers;
